import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Bot } from 'lucide-react';
import { TemporaryChatContext } from '../../Context/temporaryChatContext';
import { useParams } from 'react-router-dom';
import sessionsService from '../../services/sessionsServices';
import { AuthContext } from '../../Context/authContext';
import { useContextHook } from 'use-context-hook';
import 'swiper/css';
import 'swiper/css/navigation';
import { ChatSectionStyled, ChatColumn } from './ChatSection.styles';
import ChatMessageItem from './ChatMessageItem';
import SourceDrawer from './SourceDrawer';
import EntityModal from './EntityModal';
import PlayerChartsPanel from '../charts/PlayerChartsPanel';
import ChartsButton from '../charts/ChartsButton';
import { extractResolvedPlayers } from '../../helpers/entities';
import PolyChatHeader from './PolyChatHeader';
import PolyChatInput from './PolyChatInput';
import teamService from '../../services/teamService';
import {
  DEFAULT_COPILOT_ID,
  DEFAULT_COPILOT_NAME,
  isDefaultCopilot,
  toDefaultAwareAgentOption,
} from '../../constants/defaultCopilot';

function getAgentIconSrc(agent) {
  if (!agent?.icon) return null;
  if (
    typeof agent.icon === 'string' &&
    (agent.icon.startsWith('/') ||
      agent.icon.startsWith('http://') ||
      agent.icon.startsWith('https://') ||
      agent.icon.startsWith('data:'))
  ) {
    if (agent.icon.startsWith('http://') || agent.icon.startsWith('https://')) {
      return agent.icon.split('?')[0].toLowerCase().endsWith('.svg') ? null : agent.icon;
    }
    return agent.icon;
  }
  if (typeof agent.icon === 'string' && agent.icon.startsWith('data:')) return agent.icon;
  if (typeof agent.icon === 'string') return `data:image/png;base64,${agent.icon}`;
  return null;
}

const ChatSection = () => {
  const { user } = useContextHook(AuthContext, v => ({
    user: v.user,
  }));
  const [activeSource, setActiveSource] = useState(null);
  const [activeEntity, setActiveEntity] = useState(null);
  const [activeCharts, setActiveCharts] = useState(null);
  const [activePlayerId, setActivePlayerId] = useState(null);
  const [activeMsgInd, setActiveMsgInd] = useState(null);
  const { selectedAgent, chatMessages, setChatmessages, setSessionAgent, sessionAgent, isActiveRef, setSessionID, updateAgent } =
    useContext(TemporaryChatContext);
  const [teamDetails, setTeamDetails] = useState(null);
  const { id } = useParams();
  const hasSession = Boolean(id);

  useEffect(() => {
    const fetchTeamData = async () => {
      if (selectedAgent?.mode === 'team' && selectedAgent?.value) {
        try {
          const res = await teamService?.getTeamDetails(selectedAgent.value);
          setTeamDetails(res?.detail);
        } catch (error) {
          console.error('Failed to fetch team details:', error);
        }
      }
    };

    fetchTeamData();
  }, [selectedAgent]);

  const getSessions = async sessionId => {
    try {
      const aiResp = await sessionsService.getSessionHistory({
        user_id: user?.data?.ID,
        platform_id: 'interlabs',
        agent_id: selectedAgent?.value || DEFAULT_COPILOT_ID,
        session_id: sessionId,
      });
      if (aiResp?.agent_name || aiResp?.agent_id) {
        const agentOption = toDefaultAwareAgentOption({
          agent_name: aiResp?.agent_name,
          id: aiResp?.agent_id,
          icon: aiResp?.icon,
        });
        setSessionAgent(agentOption);
        updateAgent(agentOption);
      }
      const formattedMessages = aiResp?.history?.flatMap(item => {
        const isSource = item?.sources ? item?.sources : [];
        const websearchSources = isSource?.filter(src => src.type === 'google_search');
        const fileSources = isSource?.filter(src => src.type === 'file');
        return [
          {
            human: item.human,
            ai: item?.ai,
            isStreaming: false,
            intermediate_steps: [
              {
                tool_name: 'thinking...',
                content: item.human,
              },
              ...(item?.intermediate_steps || []),
            ],
            source: websearchSources.length > 0 ? websearchSources : [],
            files: fileSources.length > 0 ? fileSources : [],
            images: item?.images,
            isLoading: false,
          },
        ];
      });

      setChatmessages(formattedMessages);
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    if (id && isActiveRef.current) {
      getSessions(id);
      setSessionID(id);
    }
    if (!id) {
      // Fresh chat landing (/chat): clear any messages/session left over from a previous chat.
      setChatmessages([]);
      setSessionID(null);
    }
  }, [id]);

  const displayAgent = useMemo(() => {
    const raw = selectedAgent?.label ? selectedAgent : sessionAgent;
    if (!raw) return { label: DEFAULT_COPILOT_NAME, value: DEFAULT_COPILOT_ID };
    if (isDefaultCopilot(raw) || String(raw.value) === DEFAULT_COPILOT_ID) {
      return toDefaultAwareAgentOption(raw);
    }
    return raw;
  }, [selectedAgent, sessionAgent]);
  const emptyStateIcon = useMemo(() => getAgentIconSrc(displayAgent), [displayAgent]);
  const emptyTitle = displayAgent?.label || DEFAULT_COPILOT_NAME;

  const sessionAgentIcon = useMemo(() => {
    if (selectedAgent?.mode === 'team') {
      return selectedAgent?.icon;
    }
    if (isDefaultCopilot(sessionAgent) || isDefaultCopilot(selectedAgent)) {
      return getAgentIconSrc(toDefaultAwareAgentOption(sessionAgent || selectedAgent));
    }
    return getAgentIconSrc(sessionAgent);
  }, [selectedAgent, sessionAgent]);

  const lastMessageIndex = chatMessages.length - 1;
  const hasMessages = chatMessages.length > 0;

  const availableCharts = useMemo(() => {
    if (activeMsgInd == null) return null;
    const elem = chatMessages[activeMsgInd];
    if (!elem) return null;
    const players = extractResolvedPlayers(elem?.intermediate_steps, elem?.ai);
    if (!players.length) return null;
    return { players, query: elem?.human || '', responseText: elem?.ai || '' };
  }, [chatMessages, activeMsgInd]);

  const openCharts = () => {
    if (!availableCharts) return;
    setActiveCharts(availableCharts);
    setActivePlayerId(availableCharts.players[0]?.id ?? null);
  };

  return (
    <ChatSectionStyled>
      <ChatColumn className="poly-chat-layout">
        <PolyChatHeader />

        <div className="text-block poly-chat-messages">
          {!hasMessages ? (
            <div className="poly-chat-empty">
              <div className="w-16 h-16 rounded-full bg-space/10 flex items-center justify-center mb-4 overflow-hidden">
                {emptyStateIcon ? (
                  <img src={emptyStateIcon} alt="" className="w-full h-full object-cover" />
                ) : (
                  <Bot className="w-8 h-8 text-space" />
                )}
              </div>
              <h3 className="text-xl font-extrabold text-ink mb-2 tracking-tight">{emptyTitle}</h3>
              <p className="text-sm text-mute max-w-md text-center">
                {hasSession
                  ? 'Your conversational interface with Jose. Send a message to begin.'
                  : emptyTitle
                    ? `Click New Chat to start a session with ${emptyTitle}.`
                    : 'Select an agent, then click New Chat to create a session before messaging.'}
              </p>
            </div>
          ) : (
            <div className="poly-chat-thread">
              {chatMessages?.map((elem, ind) => (
                <ChatMessageItem
                  key={`${id}-${ind}-${elem.human?.slice(0, 20) ?? ''}`}
                  elem={elem}
                  ind={ind}
                  user={user}
                  selectedAgent={selectedAgent}
                  teamDetails={teamDetails}
                  sessionAgentIcon={sessionAgentIcon}
                  onSelectSource={setActiveSource}
                  onSelectEntity={setActiveEntity}
                  onVisible={setActiveMsgInd}
                  isLast={ind === lastMessageIndex}
                />
              ))}
            </div>
          )}
        </div>

        <div className="poly-chat-input">
          <div className="poly-chat-search">
            <PolyChatInput sessionId={id} createSession={false} />
          </div>
        </div>
      </ChatColumn>

      <SourceDrawer source={activeSource} onClose={() => setActiveSource(null)} />
      <EntityModal
        entity={activeEntity?.entity}
        results={activeEntity?.results}
        onClose={() => setActiveEntity(null)}
      />
      <ChartsButton
        variant="tab"
        available={!!availableCharts}
        open={!!activeCharts}
        onToggle={openCharts}
      />
      {activeCharts && (
        <PlayerChartsPanel
          players={activeCharts.players}
          activeId={activePlayerId}
          query={activeCharts.query}
          responseText={activeCharts.responseText}
          onSelect={setActivePlayerId}
          onClose={() => setActiveCharts(null)}
        />
      )}
    </ChatSectionStyled>
  );
};

export default ChatSection;
